import type { ThreekitEmbedHandle } from "../src/embed-api";
import ThreekitEmbed from "../src/main";
import {
  attachAnimationToggleDock,
  type AnimationToggleDockOptions,
} from "../src/animation-toggle-dock";
import {
  attachArLaunchDock,
  type ArLaunchDockOptions,
} from "../src/ar-launch-dock";
import {
  attachDisclaimerOverlay,
  type DisclaimerOverlayOptions,
} from "../src/disclaimer-overlay";
import {
  EMBED_CREDENTIALS_ERROR,
  analyticsHostForThreekitEnv,
  readCpqEnvDefaults,
} from "./cpq-credentials";

export type { ThreekitEmbedHandle };

export interface CpqMountOptions {
  /**
   * Threekit player environment subdomain (e.g. `preview`).
   * Defaults to `VITE_THREEKIT_ENV` / `.env` or dev query `env`.
   */
  env?: string;
  /**
   * Public player token. Defaults to `VITE_THREEKIT_AUTH_TOKEN` or dev query `token`.
   */
  authToken?: string;
  /** Catalog item id. Defaults to `VITE_THREEKIT_ASSET_ID` or dev query `asset`. */
  assetId?: string;
  /**
   * Org UUID for `@threekit/analytics`. Defaults to `VITE_THREEKIT_ORG_ID` or dev query `org`.
   * Also identifies the environment: `env` + `orgId` are forwarded to the AR QR endpoints and the
   * snapshot upload proxy, which match them against config/threekit-environments.json
   * (dev = preview, staging / prod = admin-fts with different orgs) to pick the right tokens.
   */
  orgId?: string;
  /** Override default analytics console logging */
  analyticsLog?: boolean;
  /**
   * Base https origin for this app's own server (e.g. `https://pridemobility.3kit.com`) —
   * needed for **direct CPQ embed**, where this script runs on CPQ's own document, so a plain
   * relative path (the default for each option below) would resolve against CPQ's origin
   * instead of this app's. When set, derives `filesUploadProxyPath`, `arLaunchDock.qrEndpoint`,
   * and `arQrUrlEndpoint` as `{serverPath}/api/threekit-files`, `{serverPath}/api/ar/qr`, and
   * `{serverPath}/api/ar/qrUrl` — pass any of those three explicitly to override just that one.
   */
  serverPath?: string;
  /**
   * Same-origin path for snapshot file uploads (e.g. `/api/threekit-files`).
   * Server adds `THREEKIT_API_TOKEN` when forwarding to Threekit.
   */
  filesUploadProxyPath?: string;
  /**
   * Attach the left-side animation / overlay toggle dock after mount (default `true`).
   * Pass `false` to skip; you can still call `ThreekitCpqApp.attachAnimationToggleDock` manually.
   */
  showAnimationDock?: boolean;
  /**
   * Element (or selector) that should receive the dock overlay. Defaults to the player
   * container itself — we never reach outside the element you hand us, since its parent
   * may belong to your page's own layout.
   */
  animationDockShell?: string | HTMLElement;
  /** Attribute names / options for the dock when `showAnimationDock` is enabled. */
  animationDock?: AnimationToggleDockOptions;
  /**
   * Attach the "View in AR" button + QR modal after mount (default `true`). Our own AR entry
   * point (see docs/AR-HOSTING-SPEC.md); requires the server routes in `server/ar-service.mjs`.
   * Pass `false` to skip.
   */
  showArDock?: boolean;
  /** Element (or selector) for the AR dock button. Defaults to `animationDockShell` / the player container. */
  arDockShell?: string | HTMLElement;
  /**
   * Overrides for the AR dock (QR endpoint, image format, saved-configuration metadata).
   * `qrEndpoint` defaults to `{serverPath}/api/ar/qr` when `serverPath` is set.
   */
  arLaunchDock?: Omit<ArLaunchDockOptions, "env" | "orgId" | "assetId">;
  /**
   * Path to the AR QR-upload endpoint used by `handle.generateArQrCodeAndUpload()`. Defaults to
   * `{serverPath}/api/ar/qrUrl` when `serverPath` is set, else `VITE_THREEKIT_AR_QR_URL_PROXY` /
   * `.env`, else `/api/ar/qrUrl`.
   */
  arQrUrlEndpoint?: string;
  /**
   * Show the "for visualization purposes only" legal disclaimer strip along the bottom of the
   * player (default `true`). Pass `false` to skip.
   */
  showDisclaimer?: boolean;
  /** Element (or selector) for the disclaimer strip. Defaults to `animationDockShell` / the player container. */
  disclaimerShell?: string | HTMLElement;
  /** Overrides for the disclaimer overlay (e.g. custom text). */
  disclaimer?: DisclaimerOverlayOptions;

  customId?: string;
  stageId?: string;
  branch?: string;
  publishStage?: "published" | "draft";
  display?: "webgl" | "image";
  showConfigurator?: boolean;
  showAR?: boolean;
  showShare?: boolean;
  locale?: string;
  analyticsCustomId?: string;
  configuration?: unknown;
  initialConfiguration?: unknown;
  configurationId?: string;
}

function resolveElement(
  ref: string | HTMLElement,
  label: string
): HTMLElement {
  if (typeof ref !== "string") return ref;
  const found = document.querySelector(ref);
  if (!found || !(found instanceof HTMLElement)) {
    throw new Error(`Threekit CPQ mount: ${label} not found (${ref})`);
  }
  return found;
}

function resolveDockShell(
  playerEl: HTMLElement,
  shellOpt?: string | HTMLElement
): HTMLElement {
  if (shellOpt != null) {
    return resolveElement(shellOpt, "dockShell");
  }
  return playerEl;
}

/**
 * Mount the Threekit player + analytics for CPQ flows. Pass `authToken`, `assetId`, `orgId`, etc.
 * to override `.env`; analytics reuses `authToken` and host `${env}.threekit.com`.
 * By default also attaches the animation toggle dock (see `showAnimationDock`).
 */
export async function mount(
  container: string | HTMLElement,
  options: CpqMountOptions = {}
): Promise<ThreekitEmbedHandle> {
  const defaults = readCpqEnvDefaults();

  const env = options.env ?? defaults.env;
  const authToken = options.authToken ?? defaults.authToken;
  const assetId = options.assetId ?? defaults.assetId;
  const orgId = options.orgId ?? defaults.orgId;
  const analyticsHost = analyticsHostForThreekitEnv(env);
  const serverPath = options.serverPath?.trim().replace(/\/+$/, "") || "";

  const filesUploadProxyPath =
    (
      options.filesUploadProxyPath ??
      (serverPath ? `${serverPath}/api/threekit-files` : undefined) ??
      defaults.filesUploadProxyPath
    )?.trim() || undefined;
  const arQrEndpoint =
    (
      options.arLaunchDock?.qrEndpoint ??
      (serverPath ? `${serverPath}/api/ar/qr` : undefined) ??
      defaults.arQrEndpoint
    )?.trim() || "/api/ar/qr";
  const arQrUrlEndpoint =
    (
      options.arQrUrlEndpoint ??
      (serverPath ? `${serverPath}/api/ar/qrUrl` : undefined) ??
      defaults.arQrUrlEndpoint
    )?.trim() || "/api/ar/qrUrl";

  if (!authToken || !assetId || !orgId) {
    throw new Error(EMBED_CREDENTIALS_ERROR);
  }

  const playerEl = resolveElement(container, "container");

  const handle = await ThreekitEmbed.init({
    container: playerEl,
    threekit: {
      env,
      authToken,
      assetId,
      filesUploadProxyPath,
      arQrUrlEndpoint,
      customId: options.customId,
      stageId: options.stageId,
      branch: options.branch,
      publishStage: options.publishStage ?? "published",
      display: options.display,
      showConfigurator: options.showConfigurator ?? true,
      // Default off: our own AR dock (below) fully owns the AR entry point, since Threekit's
      // native AR button has no way to point its QR code anywhere but this (CPQ-authenticated)
      // page. See docs/AR-HOSTING-SPEC.md.
      showAR: options.showAR ?? false,
      showShare: options.showShare,
      locale: options.locale,
      analyticsCustomId: options.analyticsCustomId,
      configuration: options.configuration,
      initialConfiguration: options.initialConfiguration,
      configurationId: options.configurationId,
    },
    analytics: {
      orgId,
      publicToken: authToken,
      host: analyticsHost,
      log:
        options.analyticsLog !== undefined
          ? options.analyticsLog
          : import.meta.env.DEV,
    },
  });

  let detachDock: (() => void) | null = null;
  if (options.showAnimationDock !== false) {
    const shell = resolveDockShell(playerEl, options.animationDockShell);
    const dock = attachAnimationToggleDock(
      handle,
      shell,
      options.animationDock
    );
    detachDock = () => dock.detach();
  }

  let detachArDock: (() => void) | null = null;
  if (options.showArDock !== false) {
    const shell = resolveDockShell(
      playerEl,
      options.arDockShell ?? options.animationDockShell
    );
    const arDock = attachArLaunchDock(handle, shell, {
      ...options.arLaunchDock,
      env,
      orgId,
      qrEndpoint: arQrEndpoint,
    });
    detachArDock = () => arDock.detach();
  }

  let detachDisclaimer: (() => void) | null = null;
  if (options.showDisclaimer !== false) {
    const shell = resolveDockShell(
      playerEl,
      options.disclaimerShell ?? options.animationDockShell
    );
    const disclaimer = attachDisclaimerOverlay(shell, options.disclaimer);
    detachDisclaimer = () => disclaimer.detach();
  }

  const destroyPlayer = handle.destroy.bind(handle);
  handle.destroy = () => {
    detachDock?.();
    detachDock = null;
    detachArDock?.();
    detachArDock = null;
    detachDisclaimer?.();
    detachDisclaimer = null;
    destroyPlayer();
  };

  return handle;
}
